// ===== 일진(日辰) 계산 + 길흉 판단 엔진 =====

import {
  HEAVENLY_STEMS, HEAVENLY_STEMS_KOR,
  EARTHLY_BRANCHES, EARTHLY_BRANCHES_KOR,
  STEM_ELEMENTS, BRANCH_ELEMENTS,
  type Element, type SajuResult,
  getJulianDay,
} from './sajuCalculations';

// ===== 일진 간지 계산 =====
export interface DailyGanzhi {
  stemIdx: number;
  branchIdx: number;
  stem: string;
  branch: string;
  stemKor: string;
  branchKor: string;
  stemElement: Element;
  branchElement: Element;
}

export function getDailyGanzhi(year: number, month: number, day: number): DailyGanzhi {
  const jd = getJulianDay(year, month, day);
  // 1995년 1월 6일 = 丁酉(dayIdx 33) 검증된 기준
  const dayIdx = ((jd - 2451545 + 54) % 60 + 60) % 60;
  const si = dayIdx % 10;
  const bi = dayIdx % 12;
  return {
    stemIdx: si, branchIdx: bi,
    stem: HEAVENLY_STEMS[si], branch: EARTHLY_BRANCHES[bi],
    stemKor: HEAVENLY_STEMS_KOR[si], branchKor: EARTHLY_BRANCHES_KOR[bi],
    stemElement: STEM_ELEMENTS[si], branchElement: BRANCH_ELEMENTS[bi],
  };
}

// ===== 지지 관계 =====
// 지지 육합(六合): 자-축, 인-해, 묘-술, 진-유, 사-신, 오-미
const LIUHE: [number, number][] = [[0,1],[2,11],[3,10],[4,9],[5,8],[6,7]];
// 지지 삼합(三合): 인오술(화), 사유축(금), 신자진(수), 해묘미(목)
const SANHE: [number, number, number][] = [[2,6,10],[5,9,1],[8,0,4],[11,3,7]];
// 지지 충(六沖): 자-오, 축-미, 인-신, 묘-유, 진-술, 사-해
const CHONG: [number, number][] = [[0,6],[1,7],[2,8],[3,9],[4,10],[5,11]];
// 지지 형(刑): 인-사-신, 축-술-미, 자-묘
const XING: [number, number][] = [[2,5],[2,8],[5,8],[1,10],[1,7],[10,7],[0,3]];

// 천간 합(天干合): 갑-기, 을-경, 병-신, 정-임, 무-계
const STEM_HE: [number, number][] = [[0,5],[1,6],[2,7],[3,8],[4,9]];

function branchRelation(a: number, b: number): 'liuhe' | 'sanhe' | 'chong' | 'xing' | 'none' {
  if (CHONG.some(([x, y]) => (x === a && y === b) || (x === b && y === a))) return 'chong';
  if (XING.some(([x, y]) => (x === a && y === b) || (x === b && y === a))) return 'xing';
  if (LIUHE.some(([x, y]) => (x === a && y === b) || (x === b && y === a))) return 'liuhe';
  if (SANHE.some(g => g.includes(a) && g.includes(b))) return 'sanhe';
  return 'none';
}

function stemRelation(a: number, b: number): 'he' | 'none' {
  return STEM_HE.some(([x, y]) => (x === a && y === b) || (x === b && y === a)) ? 'he' : 'none';
}

// 오행 상생: 목→화→토→금→수→목
const GENERATES = [1, 2, 3, 4, 0];
// 오행 상극: 목→토, 화→금, 토→수, 금→목, 수→화
const CONTROLS = [2, 3, 4, 0, 1];

const ELEMENT_IDX: Record<Element, number> = { 목: 0, 화: 1, 토: 2, 금: 3, 수: 4 };

// ===== 길흉 점수 계산 (−5 ~ +5) =====
export type LuckLevel = 'great' | 'good' | 'neutral' | 'caution' | 'bad';

export interface DayLuck {
  score: number;
  level: LuckLevel;
  reasons: string[];
}

export function calcDayLuck(dayGanzhi: DailyGanzhi, sajuResult: SajuResult): DayLuck {
  let score = 0;
  const reasons: string[] = [];

  const userDayStem = sajuResult.dayPillar.stemIdx;
  const userDayBranch = sajuResult.dayPillar.branchIdx;
  const userMonthBranch = sajuResult.monthPillar.branchIdx;
  const userYearBranch = sajuResult.yearPillar.branchIdx;

  // ── 천간 관계 ──
  const sr = stemRelation(dayGanzhi.stemIdx, userDayStem);
  if (sr === 'he') { score += 2; reasons.push('천간합(天干合) — 좋은 흐름'); }

  // 오행 상생/상극
  const dayStEl = ELEMENT_IDX[dayGanzhi.stemElement];
  const userStEl = ELEMENT_IDX[STEM_ELEMENTS[userDayStem]];
  if (GENERATES[dayStEl] === userStEl) { score += 1; reasons.push('천간 상생'); }
  if (GENERATES[userStEl] === dayStEl) { score += 1; reasons.push('천간 상생'); }
  if (CONTROLS[dayStEl] === userStEl) { score -= 1; reasons.push('천간 상극'); }
  if (CONTROLS[userStEl] === dayStEl) { score -= 1; reasons.push('천간 상극'); }
  if (dayStEl === userStEl) { score += 1; reasons.push('같은 오행 — 비화'); }

  // ── 지지 관계 (일지 기준) ──
  const br = branchRelation(dayGanzhi.branchIdx, userDayBranch);
  if (br === 'liuhe')  { score += 3; reasons.push('지지 육합(六合) — 최고의 날!'); }
  if (br === 'sanhe')  { score += 2; reasons.push('지지 삼합(三합) — 좋은 날'); }
  if (br === 'chong')  { score -= 3; reasons.push('지지 충(沖) — 조심하는 날'); }
  if (br === 'xing')   { score -= 2; reasons.push('지지 형(刑) — 주의가 필요한 날'); }

  // ── 월지·년지 관계 추가 보정 ──
  const brMonth = branchRelation(dayGanzhi.branchIdx, userMonthBranch);
  if (brMonth === 'liuhe') { score += 1; reasons.push('월지 육합'); }
  if (brMonth === 'chong') { score -= 1; reasons.push('월지 충'); }

  const brYear = branchRelation(dayGanzhi.branchIdx, userYearBranch);
  if (brYear === 'liuhe') { score += 1; reasons.push('년지 육합'); }
  if (brYear === 'chong') { score -= 1; reasons.push('년지 충'); }

  // 범위 클램프
  score = Math.max(-5, Math.min(5, score));

  let level: LuckLevel;
  if (score >= 4)       level = 'great';
  else if (score >= 2)  level = 'good';
  else if (score >= -1) level = 'neutral';
  else if (score >= -3) level = 'caution';
  else                  level = 'bad';

  return { score, level, reasons: reasons.filter((v, i, a) => a.indexOf(v) === i) };
}

// ===== 월간 일진 일괄 생성 =====
export interface MonthDayData {
  year: number;
  month: number;
  day: number;
  weekday: number;   // 0=일, 1=월, ..., 6=토
  ganzhi: DailyGanzhi;
  luck: DayLuck;
  isToday: boolean;
}

export function getMonthIljin(
  year: number,
  month: number,
  sajuResult: SajuResult,
): MonthDayData[] {
  const daysInMonth = new Date(year, month, 0).getDate();
  const today = new Date();
  const result: MonthDayData[] = [];

  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month - 1, day);
    const ganzhi = getDailyGanzhi(year, month, day);
    const luck = calcDayLuck(ganzhi, sajuResult);
    result.push({
      year, month, day,
      weekday: d.getDay(),
      ganzhi, luck,
      isToday:
        today.getFullYear() === year &&
        today.getMonth() + 1 === month &&
        today.getDate() === day,
    });
  }
  return result;
}

export const LUCK_STYLES: Record<LuckLevel, { dot: string; bg: string; text: string; label: string; icon: string }> = {
  great:   { dot: '#10B981', bg: '#D1FAE5', text: '#065F46', label: '대길', icon: '●' },
  good:    { dot: '#34D399', bg: '#ECFDF5', text: '#065F46', label: '길',   icon: '●' },
  neutral: { dot: '#9CA3AF', bg: 'transparent', text: '#6B7280', label: '평', icon: '·' },
  caution: { dot: '#F59E0B', bg: '#FFF7ED', text: '#92400E', label: '주의', icon: '▲' },
  bad:     { dot: '#EF4444', bg: '#FEF2F2', text: '#991B1B', label: '흉',   icon: '▲' },
};

// English version
export const LUCK_STYLES_EN: Record<LuckLevel, { dot: string; bg: string; text: string; label: string; icon: string }> = {
  great:   { dot: '#10B981', bg: '#D1FAE5', text: '#065F46', label: 'Great',   icon: '●' },
  good:    { dot: '#34D399', bg: '#ECFDF5', text: '#065F46', label: 'Good',    icon: '●' },
  neutral: { dot: '#9CA3AF', bg: 'transparent', text: '#6B7280', label: 'Neutral', icon: '·' },
  caution: { dot: '#F59E0B', bg: '#FFF7ED', text: '#92400E', label: 'Caution', icon: '▲' },
  bad:     { dot: '#EF4444', bg: '#FEF2F2', text: '#991B1B', label: 'Bad',     icon: '▲' },
};

// English reason translations
const REASONS_EN: Record<string, string> = {
  '천간합(天干合) — 좋은 흐름': 'Stem Harmony — Good flow',
  '천간 상생': 'Stem Generates',
  '천간 상극': 'Stem Controls',
  '같은 오행 — 비화': 'Same Element — Mutual support',
  '지지 육합(六合) — 최고의 날!': 'Branch Harmony — Best day!',
  '지지 삼합(三合) — 좋은 날': 'Branch Triple Harmony — Good day',
  '지지 충(沖) — 조심하는 날': 'Branch Clash — Caution needed',
  '지지 형(刑) — 주의가 필요한 날': 'Branch Punishment — Be careful',
  '월지 육합': 'Month Branch Harmony',
  '월지 충': 'Month Branch Clash',
  '년지 육합': 'Year Branch Harmony',
  '년지 충': 'Year Branch Clash',
};

export function calcDayLuckEn(dayGanzhi: DailyGanzhi, sajuResult: SajuResult): DayLuck {
  const luck = calcDayLuck(dayGanzhi, sajuResult);
  return {
    ...luck,
    reasons: luck.reasons.map(r => REASONS_EN[r] || r),
  };
}

export function getMonthIljinEn(
  year: number,
  month: number,
  sajuResult: SajuResult,
): MonthDayData[] {
  const daysInMonth = new Date(year, month, 0).getDate();
  const today = new Date();
  const result: MonthDayData[] = [];

  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month - 1, day);
    const ganzhi = getDailyGanzhi(year, month, day);
    const luck = calcDayLuckEn(ganzhi, sajuResult);
    result.push({
      year, month, day,
      weekday: d.getDay(),
      ganzhi, luck,
      isToday:
        today.getFullYear() === year &&
        today.getMonth() + 1 === month &&
        today.getDate() === day,
    });
  }
  return result;
}
